import { create } from 'zustand';

export const COLLABORATION_HIGHLIGHT_TTL_MS = 10 * 60 * 1000;

export type CollaborationHighlightContext = 'project_page' | 'parse_flow' | 'canvas';

export type CollaborationHighlightTargetType =
  | 'storyboard'
  | 'storyboard_item'
  | 'resource_element'
  | 'resource_tag'
  | 'project_resource'
  | 'dialogue'
  | 'storyboard_image'
  | 'storyboard_video'
  | 'voiceover'
  | 'sound_effect'
  | 'review_diff'
  | 'screenplay_scene'
  | 'parse_flow_storyboard'
  | 'resource_pin'
  | 'temporary_video';

export type CollaborationHighlightType = 'new' | 'updated' | 'attention' | 'ready' | 'removed';

export interface CollaborationHighlightEvent {
  eventId: string;
  projectId: string;
  screenplayId?: string | null;
  context: CollaborationHighlightContext;
  targetType: CollaborationHighlightTargetType;
  targetId: string;
  targetProjectId?: string | null;
  affectedProjectIds?: string[];
  targetStoryboardId?: string | null;
  elementType?: string | null;
  changeType?: string | null;
  highlightType: CollaborationHighlightType;
  actor: {
    userId: string;
    displayName?: string | null;
  };
  summary?: string | null;
  batchId?: string | null;
  occurredAt: string;
}

export interface CollaborationHighlightMatch {
  projectId: string;
  parentProjectId?: string | null;
  context: CollaborationHighlightContext;
  targetType: CollaborationHighlightTargetType;
  targetId: string;
  dismissedScope?: string;
}

export interface CollaborationHighlightAnyMatch {
  projectId: string;
  parentProjectId?: string | null;
  context: CollaborationHighlightContext;
  targetTypes?: readonly CollaborationHighlightTargetType[];
  elementTypes?: readonly string[];
  dismissedScope?: string;
}

export interface CollaborationHighlightStoryboardScopedMatch {
  projectId: string;
  parentProjectId?: string | null;
  context: CollaborationHighlightContext;
  targetStoryboardId: string;
  targetTypes: readonly CollaborationHighlightTargetType[];
  dismissedScope?: string;
}

interface StoredHighlight {
  event: CollaborationHighlightEvent;
  expiresAt: number;
}

interface CollaborationHighlightState {
  byEventId: Record<string, StoredHighlight>;
  dismissedScopes: Record<string, number>;
  addHighlight: (event: CollaborationHighlightEvent, now?: number) => void;
  clearExpired: (now?: number) => void;
  hasHighlight: (match: CollaborationHighlightMatch, now?: number) => boolean;
  hasAnyHighlight: (match: CollaborationHighlightAnyMatch, now?: number) => boolean;
  hasStoryboardScopedHighlight: (
    match: CollaborationHighlightStoryboardScopedMatch,
    now?: number
  ) => boolean;
  clearHighlight: (match: Partial<CollaborationHighlightMatch>) => void;
  dismissScope: (scope: string) => void;
  reset: () => void;
}

function matchesProjectScope(
  event: CollaborationHighlightEvent,
  projectId: string,
  parentProjectId?: string | null
): boolean {
  return (
    event.projectId === projectId ||
    event.targetProjectId === projectId ||
    Boolean(event.affectedProjectIds?.includes(projectId)) ||
    (parentProjectId != null && event.projectId === parentProjectId)
  );
}

function matchesHighlight(
  event: CollaborationHighlightEvent,
  match: CollaborationHighlightMatch
): boolean {
  return (
    matchesProjectScope(event, match.projectId, match.parentProjectId) &&
    event.context === match.context &&
    event.targetType === match.targetType &&
    event.targetId === match.targetId
  );
}

function matchesStoryboardScopedHighlight(
  event: CollaborationHighlightEvent,
  match: CollaborationHighlightStoryboardScopedMatch
): boolean {
  return (
    matchesProjectScope(event, match.projectId, match.parentProjectId) &&
    event.context === match.context &&
    event.targetStoryboardId === match.targetStoryboardId &&
    match.targetTypes.includes(event.targetType)
  );
}

function getEventTime(event: CollaborationHighlightEvent): number {
  const timestamp = Date.parse(event.occurredAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function isDismissedForScope(
  event: CollaborationHighlightEvent,
  dismissedScopes: Record<string, number>,
  scope?: string
): boolean {
  if (!scope) return false;
  const dismissedAt = dismissedScopes[scope];
  return typeof dismissedAt === 'number' && getEventTime(event) <= dismissedAt;
}

export const useCollaborationHighlightStore = create<CollaborationHighlightState>((set, get) => ({
  byEventId: {},
  dismissedScopes: {},
  addHighlight: (event, now = Date.now()) => {
    set((state) => ({
      byEventId: {
        ...state.byEventId,
        [event.eventId]: {
          event,
          expiresAt: now + COLLABORATION_HIGHLIGHT_TTL_MS,
        },
      },
    }));
  },
  clearExpired: (now = Date.now()) => {
    const nextEntries = Object.entries(get().byEventId).filter(([, item]) => item.expiresAt > now);
    set({ byEventId: Object.fromEntries(nextEntries) });
  },
  hasHighlight: (match, now = Date.now()) => {
    const state = get();
    return Object.values(state.byEventId).some(
      (item) =>
        item.expiresAt > now &&
        !isDismissedForScope(item.event, state.dismissedScopes, match.dismissedScope) &&
        matchesHighlight(item.event, match)
    );
  },
  hasAnyHighlight: (match, now = Date.now()) => {
    const targetTypes = match.targetTypes ? new Set(match.targetTypes) : null;
    const elementTypes = match.elementTypes ? new Set(match.elementTypes) : null;
    const state = get();
    return Object.values(state.byEventId).some(
      (item) =>
        item.expiresAt > now &&
        !isDismissedForScope(item.event, state.dismissedScopes, match.dismissedScope) &&
        matchesProjectScope(item.event, match.projectId, match.parentProjectId) &&
        item.event.context === match.context &&
        (!targetTypes || targetTypes.has(item.event.targetType)) &&
        (!elementTypes ||
          (item.event.elementType ? elementTypes.has(item.event.elementType) : false))
    );
  },
  hasStoryboardScopedHighlight: (match, now = Date.now()) => {
    const state = get();
    return Object.values(state.byEventId).some(
      (item) =>
        item.expiresAt > now &&
        !isDismissedForScope(item.event, state.dismissedScopes, match.dismissedScope) &&
        matchesStoryboardScopedHighlight(item.event, match)
    );
  },
  clearHighlight: (match) => {
    const nextEntries = Object.entries(get().byEventId).filter(([, item]) => {
      const event = item.event;
      if (
        match.projectId &&
        !matchesProjectScope(event, match.projectId, match.parentProjectId ?? null)
      ) {
        return true;
      }
      if (match.context && event.context !== match.context) return true;
      if (match.targetType && event.targetType !== match.targetType) return true;
      if (match.targetId && event.targetId !== match.targetId) return true;
      return false;
    });
    set({ byEventId: Object.fromEntries(nextEntries) });
  },
  dismissScope: (scope) => {
    set((state) => ({
      dismissedScopes: {
        ...state.dismissedScopes,
        [scope]: Date.now(),
      },
    }));
  },
  reset: () => {
    set({ byEventId: {}, dismissedScopes: {} });
  },
}));
